import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { CURRENT_SHOW, LOTS } from './data';

// HOME — museum wall layout. ENORMOUS whitespace. Single hero painting that takes
// the right column. Programme list as a typographic ledger.
export default function Home({ setPage }) {
  return (
    <div className="bg-[#F4F1EA] text-[#0A0A0A]">
      {/* Hero: title left, painting right, very gallery-wall */}
      <section className="max-w-[1500px] mx-auto px-8 lg:px-14 pt-12 pb-24 grid lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-20">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="self-end pb-2">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B53024] mb-6">— Current exhibition</p>
          <h1 className="font-serif text-[42px] sm:text-[64px] lg:text-[88px] leading-[1.02] tracking-tight">{CURRENT_SHOW.title.split(':')[0]}: <span className="italic block text-[#B53024]">{CURRENT_SHOW.title.split(':')[1]}</span></h1>
          <p className="font-serif italic text-lg lg:text-xl text-[#0A0A0A]/65 mt-6 max-w-md leading-[1.5]" dangerouslySetInnerHTML={{ __html: CURRENT_SHOW.curator }} />
          <div className="border-t border-[#0A0A0A]/15 mt-10 pt-5 grid grid-cols-2 gap-4 font-mono text-[11px] max-w-md">
            <div><p className="text-[#B53024] tracking-[0.25em] uppercase mb-1 text-[10px]">Dates</p><p className="text-[#0A0A0A]">{CURRENT_SHOW.dates}</p></div>
            <div><p className="text-[#B53024] tracking-[0.25em] uppercase mb-1 text-[10px]">Location</p><p className="text-[#0A0A0A]">{CURRENT_SHOW.loc}</p></div>
          </div>
          <button onClick={() => setPage('catalogue')} className="mt-10 group inline-flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#B53024] hover:text-[#0A0A0A]" data-testid="art-home-catalogue-cta">
            View the lot index <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5" />
          </button>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4 }} className="relative">
          {/* "Wall" with painting hung */}
          <div className="bg-[#EDE6D8] p-8 lg:p-16">
            <div className="aspect-[3/4] overflow-hidden border-8 border-[#0A0A0A]/15">
              <img src={CURRENT_SHOW.hero} alt="" className="w-full h-full object-cover" />
            </div>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0A0A0A]/55 mt-4">Featured · Vilhelm Hammershøi (attr.) · 1909</p>
          </div>
        </motion.div>
      </section>

      {/* Editorial intro */}
      <section className="border-y border-[#0A0A0A]/10 bg-[#EDE6D8]">
        <div className="max-w-[920px] mx-auto px-8 lg:px-14 py-20">
          <p className="font-serif text-2xl lg:text-3xl leading-[1.55] text-[#0A0A0A]/85" dangerouslySetInnerHTML={{ __html: CURRENT_SHOW.intro }} />
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#0A0A0A]/45 mt-8">From the curator's introduction</p>
        </div>
      </section>

      {/* Programme calendar — table with red accents, NOT cards */}
      <section className="max-w-[1500px] mx-auto px-8 lg:px-14 py-24">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-20">
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B53024] mb-3">— Programme</p>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[0.98]">A year of <span className="italic text-[#B53024]">three exhibitions</span>.</h2>
          </div>
          <table className="w-full font-serif">
            <tbody>
              {[
                { d: 'Feb — May', t: 'Quiet Light: Northern Painters 1880&ndash;1940', loc: 'New York · current', state: 'Open' },
                { d: 'Jun — Sep', t: 'A Single Drawing: Late Egon Schiele', loc: 'London · forthcoming', state: 'Forthcoming' },
                { d: 'Oct — Dec', t: 'Halcyon Auction · Twentieth Century', loc: 'Both galleries · live-stream', state: 'Auction' },
              ].map((p, i) => (
                <tr key={i} className="border-t border-[#0A0A0A]/15">
                  <td className="py-6 pr-4 align-top w-32 lg:w-40 font-mono text-[10px] tracking-[0.3em] uppercase text-[#B53024]">{p.d}</td>
                  <td className="py-6 pr-4">
                    <p className="text-2xl lg:text-3xl leading-tight" dangerouslySetInnerHTML={{ __html: p.t }} />
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#0A0A0A]/50 mt-2">{p.loc}</p>
                  </td>
                  <td className="py-6 align-top text-right font-mono text-[10px] tracking-[0.25em] uppercase">
                    <span className={`px-2.5 py-1 ${p.state === 'Open' ? 'bg-[#B53024] text-[#F4F1EA]' : 'border border-[#0A0A0A]/30 text-[#0A0A0A]/65'}`}>{p.state}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Three featured lot teasers — large numbered list, single column on right */}
      <section className="border-t border-[#0A0A0A]/10 bg-[#0A0A0A] text-[#F4F1EA] py-24">
        <div className="max-w-[1500px] mx-auto px-8 lg:px-14 grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-20">
          <div className="self-start lg:sticky lg:top-32">
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B53024] mb-4">— Highlights · Auction Oct 2026</p>
            <h2 className="font-serif text-4xl lg:text-7xl leading-[0.95]">Three lots, <span className="italic text-[#B53024]">already on the wall</span>.</h2>
            <p className="text-[#F4F1EA]/65 leading-[1.7] mt-8 max-w-md">Public viewing begins six weeks before the hammer. Private viewings begin earlier &mdash; ask us.</p>
            <button onClick={() => setPage('catalogue')} className="mt-10 font-mono text-[10px] tracking-[0.3em] uppercase border border-[#B53024] text-[#B53024] px-5 py-2.5 hover:bg-[#B53024] hover:text-[#F4F1EA]">Open lot index →</button>
          </div>
          <div className="space-y-12">
            {LOTS.slice(0, 3).map((l, i) => (
              <motion.div key={l.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="grid grid-cols-[1fr_2fr] gap-8 pb-12 border-b border-[#F4F1EA]/15 last:border-b-0 last:pb-0">
                <div className="aspect-[4/5] overflow-hidden bg-[#1A1A1A]"><img src={l.img} alt="" className="w-full h-full object-cover" loading="lazy" /></div>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#B53024]">{l.n} · Estimate</p>
                  <p className="font-serif text-2xl lg:text-3xl mt-2 leading-tight" dangerouslySetInnerHTML={{ __html: l.title }} />
                  <p className="font-serif italic text-base text-[#F4F1EA]/55 mt-2">{l.med}</p>
                  <p className="font-serif text-2xl text-[#B53024] mt-5">{l.est}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
